import { Injectable, inject } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

import { AlertController, LoadingController } from "@ionic/angular";

// models

import { TypeLogout } from "@models/I_DecouplingLogout";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { C_Customization } from "@sports-models/index";

import { EventsService } from "@providers/EventsService";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";

@Injectable({ providedIn: "root" })
export class Utils {
  alertOpened = false;
  globalVars!: GlobalVarsModel;
  loading: any;
  isLoading = false;
  isMobile: boolean;

  //#region Inject Services
  // public parseMoney: ParseMoney,
  alertController = inject(AlertController);
  loadingCtrl = inject(LoadingController);
  events = inject(EventsService);
  newBaseService = inject(NewBaseService);
  device = inject(DeviceService);

  constructor() {
    this.newBaseService.getVars.subscribe((data: GlobalVarsModel) => {
      this.globalVars = data;
    });
    this.isMobile = this.device.isMobile();
  }

  async alert(
    iconoWarning: boolean,
    title: string,
    message: string,
    buttonText: string,
    buttonHandler: any = null,
    cssClass = "",
    includeFooter = true,
  ) {
    if (!this.alertOpened) {
      const alert = await this.alertController.create({
        header: title,
        message: message,
        cssClass: cssClass,
        buttons: [
          {
            text: buttonText,
            handler: () => {
              if (buttonHandler) {
                buttonHandler();
                // Codigo añadido para evitar el bug 77958
                // REVISAR
                alert.dismiss();
                this.alertOpened = false;
                return false;
              }
              return null;
            },
          },
        ],
        backdropDismiss: false,
      });

      alert.onWillDismiss().then(() => {
        this.alertOpened = false;
      });

      // Presentar la alerta
      await alert.present();

      if (includeFooter && this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
        const footer = includeFooter ? await this.createARFooter() : null;
        // Agregar el pie de página después de que la alerta se haya presentado
        if (alert) {
          const alertElement = alert.querySelector(".alert-wrapper");
          if (alertElement && !alertElement.querySelector("#footer-salida")) {
            alertElement.appendChild(footer);
          }
        }
      }
      return alert;
    }
    return null;
  }

  async createARFooter(isGreen = false, isAutoClose = false): Promise<HTMLElement> {
    const salidaOculta = !this.globalVars.user.logged ? "oculto" : "";
    const footer = document.createElement("div");
    footer.classList.add("alert-footer");
    if (isGreen) {
      footer.classList.add("alertgreen");
    }

    footer.innerHTML = `
      <a external-link href="#" id="footer-salida" class="${salidaOculta}">
        <img src="assets/global/img/argentina/logosalida.png" alt="Logo Salida">
      </a>
      <a external-link target="_blank" href="https://www.saberjugar.gob.ar/">
        <img src="assets/global/img/argentina/logoLotba.svg" alt="Logo Loteria de la Ciudad">
      </a>
      <a external-link target="_blank" href="https://saberjugar.gob.ar/">
        <img src="assets/global/img/argentina/logoSJ.svg" alt="Logo Saber Jugar" class="bordery">
      </a>
    `;

    footer.querySelector("#footer-salida").addEventListener("click", (ev) => {
      ev.preventDefault();
      this.events.publish("user:logout", TypeLogout.MANUAL);
      const alert = document.querySelector("ion-alert");
      if (alert) {
        alert.dismiss();
      }
    });
    const enlaces: any = footer.querySelectorAll("[target]");
    enlaces.forEach((item) =>
      item.addEventListener("click", () => {
        if (!isAutoClose) {
          const alert = document.querySelector("ion-alert");
          if (alert) {
            alert.dismiss();
          }
        } else {
          // Al usar un enlace externo, no se cierra el alert
        }
      }),
    );

    return footer;
  }

  async confirm(
    iconoWarning: boolean,
    title: string,
    message: string,
    cancelText: string,
    cancelHandler: any = null,
    acceptText: string,
    acceptHandler: any = null,
    backdropDismiss = false,
    cssClass = "",
  ) {
    const alert = await this.alertController.create({
      header: title,
      message: "<span class='alertMessageCustom'>" + message + "</span>",
      cssClass: "sb-alert " + cssClass,
      //enableBackdropDismiss: para minimizar la alerta pulsando fuera, por defecto se minimiza.
      backdropDismiss: backdropDismiss,
      buttons: [
        {
          text: cancelText,
          role: "cancel",
          handler: cancelHandler,
        },
        {
          text: acceptText,
          handler: acceptHandler,
        },
      ],
    });

    alert.onWillDismiss().then(() => {
      this.alertOpened = false;
    });

    await alert.present();

    if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
      // Agregar el pie de página después de que la alerta se haya presentado
      const footer = await this.createARFooter();
      if (alert) {
        const alertElement = alert.querySelector(".alert-wrapper");
        if (alertElement) {
          alertElement.appendChild(footer);
        }
      }
    }
  }
  /**
   * Muestra errores llamando al método alert
   * @param msg
   * @param callback
   */
  public async showError(msg: string, callback?: any) {
    //show the error msg and callback if any
    return this.alert(true, "Error", msg, "OK", callback);
  }

  //LOADER GLOBAL
  public async loader(content?: any, cssClass = "is-loader", translucent = true) {
    if (!this.isLoading) {
      this.isLoading = true;

      this.loading = await this.loadingCtrl.create({
        spinner: "crescent",
        message: content,
        backdropDismiss: false,
        cssClass: cssClass,
        translucent: translucent,
      });

      await this.loading.present();
    }
  }

  public async closeLoader() {
    if (this.isLoading) {
      const loader = await this.loadingCtrl.getTop();
      if (loader) {
        await loader.dismiss();
        this.isLoading = false;
        this.events.publish("loader:dismiss", null);
      } else {
        this.events.publish("loader:dismiss", null);
      }
    }
  }

  public formatAlertSportsGeolocalization(alert: HTMLIonAlertElement, link?: string, linkTitle?: string) {
    if (alert) {
      const nodeIonAlert = alert;
      const nodeAlert = nodeIonAlert.querySelector(".alert-wrapper");
      nodeAlert.classList.add("alertsportsgeocaba");
      if (link) {
        const divContainer = document.createElement("div");
        divContainer.classList.add("d-flex");
        divContainer.classList.add("justify-center");

        const a = document.createElement("a");
        a.href = link;
        a.innerHTML = linkTitle;
        a.target = "_blank";

        const img = document.createElement("img");
        img.classList.add("align-self-center");
        img.src = "assets/global/img/whats.png";
        img.alt = "Codere whatsapp";
        img.width = 20;
        img.height = 20;

        divContainer.appendChild(img);
        divContainer.appendChild(a);
        nodeAlert.querySelector(".alert-message").appendChild(divContainer);
      }
      const btn = document.createElement("button");
      btn.classList.add("cta-close-btn");
      btn.innerHTML = '<i class="codere-icon icon-close cta-close-icon"></i>';
      btn.setAttribute("tappable", "");
      btn.setAttribute("ion-button", "");
      btn.addEventListener("click", () => {
        alert.dismiss({}, "x-pressed");
      });
      nodeAlert.querySelector(".alert-head").appendChild(btn);
    }
  }
  setCuotaAccept(value: string) {
    // TODO: revisar por que no hace uso del modelo actualizado
    this.globalVars.cuotaAccept = value;
    switch (value) {
      case "0":
        //this.user.customization = new C_Customization(true, true);
        this.globalVars.user.customization = new C_Customization(true, true);
        break;
      case "1":
        //this.user.customization = new C_Customization(true, false);
        this.globalVars.user.customization = new C_Customization(true, false);
        break;
      case "2":
        //this.user.customization = new C_Customization(false, false);
        this.globalVars.user.customization = new C_Customization(false, false);
        break;
    }
    this.newBaseService.setVar = this.globalVars;
  }
  async sportsAlert(
    iconoWarning: boolean,
    title: string,
    message: string,
    buttonText: string,
    buttonHandler: any = null,
    cssClass = "",
  ) {
    if (!this.alertOpened) {
      const alert = await this.alertController.create({
        header: title,
        message: message,
        cssClass: cssClass,
        buttons: [
          {
            text: buttonText,
            handler: () => {
              if (buttonHandler) {
                buttonHandler();
                // Codigo añadido para evitar el bug 77958
                // REVISAR
                alert.dismiss();
                this.alertOpened = false;
                return false;
              }
              return null;
            },
          },
        ],
        backdropDismiss: false,
      });

      alert.onWillDismiss().then(() => {
        this.alertOpened = false;
      });

      // Presentar la alerta
      await alert.present();
      this.alertOpened = true;

      if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
        const footer = await this.createARFooter();
        // Agregar el pie de página después de que la alerta se haya presentado
        if (alert) {
          const alertElement = alert.querySelector(".alert-wrapper");
          if (alertElement && !alertElement.querySelector("#footer-salida")) {
            alertElement.appendChild(footer);
          }
        }
      }
      return alert;
    }
    return null;
  }

  public showMsg(msg: string, callback?: any, title = "Info", classAlert?: string) {
    //show the error msg and callback if any
    return this.alert(true, title, msg, "OK", callback, classAlert);
  }

  setSportsScope(sportsScope) {
    this.globalVars.sportsScope = sportsScope;
  }
  /**
   * Sets the accessory bar visible/invisible, only supported on iPhone devices
   * @param visible whether the accessory bar should be visible
   */
  hideFormAccessoryBar(visible: boolean) {
    if (this.isMobile && this.device.getDevicePlatform() === "Mobile" && Capacitor.getPlatform() === "ios") {
      Keyboard.setAccessoryBarVisible({ isVisible: visible });
    }
  }
}
